import { useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Button, Tag, Space } from 'antd';
import {
  ArrowLeftOutlined,
  EyeOutlined,
  FileTextOutlined,
  ShopOutlined,
  UserOutlined
} from '@ant-design/icons';
import endpoints from '../../api/endpoints';
import { useQuery } from '../../hooks/useApi';
import { paiseToRupees, formatDate, formatDateTime } from '../../utils/format';
import ErrorAlert from '../../components/ErrorAlert';
import LoadingBlock from '../../components/LoadingBlock';
import PageHeader from '../../components/PageHeader';
import { notifyError } from '../../utils/notify';

// Primary "View report" — Crystal-Reports-style flow. Each click
// creates a FRESH temporary report_jobs row (no reuse of an old
// completed jobId) and routes the user straight to the single-
// viewer route /reports/viewer/:jobId. The legacy /reports list
// page is no longer reachable by users.
function ViewReportButton({ invoiceId }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  async function run() {
    if (busy) return;
    setBusy(true);
    try {
      const out = await endpoints.reports.createJob({
        reportCode: 'subscription_invoice',
        format: 'html',
        // Wire field is `inputParams` — mirrors the DB column
        // report_jobs.input_params. The validator also still
        // accepts legacy `params` for any other caller.
        inputParams: { invoiceId }
      });
      const jobId = out && out.job && out.job.id;
      if (jobId) navigate(`/reports/viewer/${jobId}`);
    } catch (err) {
      // Surface to the user via the notifier; do not echo the error
      // response (which may carry sensitive data) to the browser console.
      notifyError(err, 'Could not queue this invoice in the report engine.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <Button type="primary" icon={<EyeOutlined />} loading={busy} onClick={run}>
      View report
    </Button>
  );
}

function PartyCard({ icon, title, party }) {
  return (
    <div className="rounded-2xl bg-white shadow-card border border-surface-200/70 p-5">
      <h3 className="text-xs uppercase tracking-wider text-ink-500 font-semibold m-0 mb-3 flex items-center gap-2">
        <span className="text-brand-500">{icon}</span> {title}
      </h3>
      <dl className="divide-y divide-surface-200">
        <div className="flex items-start justify-between py-2 gap-3 text-sm">
          <dt className="text-ink-500 shrink-0">Legal name</dt>
          <dd className="text-ink-900 font-medium text-right">
            {party.legalName || '—'}
          </dd>
        </div>
        <div className="flex items-center justify-between py-2 gap-3 text-sm">
          <dt className="text-ink-500 shrink-0">GSTIN</dt>
          <dd>
            {party.gstin ? (
              <code className="text-xs text-ink-700">{party.gstin}</code>
            ) : (
              '—'
            )}
          </dd>
        </div>
        <div className="flex items-center justify-between py-2 gap-3 text-sm">
          <dt className="text-ink-500 shrink-0">State</dt>
          <dd className="text-ink-900 font-medium">{party.stateCode || '—'}</dd>
        </div>
        <div className="flex items-start justify-between py-2 gap-3 text-sm">
          <dt className="text-ink-500 shrink-0">Address</dt>
          <dd className="text-ink-700 text-right">{party.address || '—'}</dd>
        </div>
        {party.email ? (
          <div className="flex items-center justify-between py-2 gap-3 text-sm">
            <dt className="text-ink-500 shrink-0">Email</dt>
            <dd className="text-ink-700 truncate">{party.email}</dd>
          </div>
        ) : null}
      </dl>
    </div>
  );
}

export default function InvoiceDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, error, loading } = useQuery(() => endpoints.invoices.get(id), [id]);

  if (loading) return <LoadingBlock />;
  if (error)
    return (
      <div>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/invoices')}>
          Back to invoices
        </Button>
        <ErrorAlert error={error} style={{ marginTop: 16 }} />
      </div>
    );
  if (!data) return null;

  const inv = data;
  const seller = inv.seller || {};
  const buyer = inv.buyer || {};
  const line = inv.line || {};
  const amounts = inv.amounts || {};

  return (
    <div>
      <PageHeader
        title={inv.invoiceNumber}
        subtitle={
          <span className="flex items-center gap-2 flex-wrap">
            Tax invoice issued on{' '}
            <strong className="text-ink-700">{formatDate(inv.invoiceDate)}</strong>
            <Tag color={inv.isIntraState ? 'blue' : 'purple'}>
              {inv.isIntraState ? 'intra-state (CGST + SGST)' : 'inter-state (IGST)'}
            </Tag>
          </span>
        }
        icon={<FileTextOutlined />}
        actions={
          <Space>
            <ViewReportButton invoiceId={id} />
            <Link to="/invoices">
              <Button icon={<ArrowLeftOutlined />}>Back to invoices</Button>
            </Link>
          </Space>
        }
      />

      {/* Parties */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <PartyCard icon={<ShopOutlined />} title="Seller" party={seller} />
        <PartyCard icon={<UserOutlined />} title="Buyer" party={buyer} />
      </div>

      {/* Line item */}
      <section className="rounded-2xl bg-white shadow-card border border-surface-200/70 p-5 mb-4">
        <h3 className="text-xs uppercase tracking-wider text-ink-500 font-semibold m-0 mb-3">
          Line item
        </h3>
        <dl className="divide-y divide-surface-200">
          <div className="flex items-start justify-between py-2.5 gap-3 text-sm">
            <dt className="text-ink-500 shrink-0">Description</dt>
            <dd className="text-ink-900 font-medium text-right">
              {line.description}
            </dd>
          </div>
          <div className="flex items-center justify-between py-2.5 gap-3 text-sm">
            <dt className="text-ink-500 shrink-0">HSN/SAC</dt>
            <dd>
              <code className="text-xs text-ink-700">{line.hsnSacCode}</code>
            </dd>
          </div>
          <div className="flex items-center justify-between py-2.5 gap-3 text-sm">
            <dt className="text-ink-500 shrink-0">Period</dt>
            <dd className="text-ink-900 font-medium">
              {formatDate(inv.periodStart)} → {formatDate(inv.periodEnd)}
            </dd>
          </div>
          <div className="flex items-center justify-between py-2.5 gap-3 text-sm">
            <dt className="text-ink-500 shrink-0">Plan</dt>
            <dd>
              <Tag>
                {inv.planCode} v{inv.planVersion ?? '—'}
              </Tag>
            </dd>
          </div>
          <div className="flex items-center justify-between py-2.5 gap-3 text-sm">
            <dt className="text-ink-500 shrink-0">Cycle</dt>
            <dd>
              <Tag color="blue">{inv.billingCycle}</Tag>
            </dd>
          </div>
        </dl>
      </section>

      {/* GST Breakdown */}
      <section className="rounded-2xl bg-white shadow-card border border-surface-200/70 overflow-hidden">
        <div className="px-5 py-3 border-b border-surface-200 flex items-center justify-between">
          <h3 className="text-xs uppercase tracking-wider text-ink-500 font-semibold m-0">
            GST breakdown
          </h3>
          <span className="text-xs text-ink-500">
            Generated {formatDateTime(inv.generatedAt)}
          </span>
        </div>
        <div className="divide-y divide-surface-200">
          <div className="flex items-center justify-between px-5 py-3 text-sm">
            <span className="text-ink-500">Taxable</span>
            <span className="font-medium text-ink-900">
              {paiseToRupees(amounts.taxableAmountPaise)}
            </span>
          </div>
          {inv.isIntraState ? (
            <>
              <div className="flex items-center justify-between px-5 py-3 text-sm">
                <span className="text-ink-500">
                  CGST{' '}
                  <span className="text-ink-400">({amounts.cgstRate}%)</span>
                </span>
                <span className="font-medium text-ink-900">
                  {paiseToRupees(amounts.cgstAmountPaise)}
                </span>
              </div>
              <div className="flex items-center justify-between px-5 py-3 text-sm">
                <span className="text-ink-500">
                  SGST{' '}
                  <span className="text-ink-400">({amounts.sgstRate}%)</span>
                </span>
                <span className="font-medium text-ink-900">
                  {paiseToRupees(amounts.sgstAmountPaise)}
                </span>
              </div>
            </>
          ) : (
            <div className="flex items-center justify-between px-5 py-3 text-sm">
              <span className="text-ink-500">
                IGST <span className="text-ink-400">({amounts.igstRate}%)</span>
              </span>
              <span className="font-medium text-ink-900">
                {paiseToRupees(amounts.igstAmountPaise)}
              </span>
            </div>
          )}
          <div className="flex items-center justify-between px-5 py-4 bg-brand-50">
            <span className="text-sm font-semibold text-ink-900">Total</span>
            <span className="text-2xl font-bold text-brand-700">
              {paiseToRupees(amounts.totalAmountPaise)}{' '}
              <span className="text-sm text-brand-500 font-medium">
                {amounts.currency}
              </span>
            </span>
          </div>
          <div className="flex items-center justify-between px-5 py-3 text-sm">
            <span className="text-ink-500">Place of supply</span>
            <span className="font-medium text-ink-900">
              {inv.placeOfSupplyState}
            </span>
          </div>
        </div>
      </section>
    </div>
  );
}
